import { useState, useEffect, useRef } from "react";
import { getMessaging, getToken } from "firebase/messaging";
import { supabase } from "./supabase";
import * as XLSX from "xlsx";
import { LayoutDashboard, Clock, Calendar, Shield, Star, Users, FolderOpen, Upload, FileText, Lock, LogOut, Sun, Moon, Globe, Bell, Menu, X, ChevronRight, Search, Briefcase, User, Download, Umbrella, Pill, DollarSign, AlertTriangle, Trash2, CheckCircle, XCircle, Coffee, Loader, Cake, Paperclip, PenLine, CheckSquare, Square, Check, Phone, Mail, MapPin, BookOpen, Flag, PlaneTakeoff, UtensilsCrossed, Wrench, MessageCircle, RefreshCw } from "lucide-react";
import {
  fmtDate, fmtDateDisplay, parseDisplayDate, getHolidays, getAllocatedVac,
  POLICY, KO, EN, ES, getStyle, TYPE_EN, dispType,
  firebaseApp, FCM_VAPID_KEY, sendPushToAdmin, sendPushToEmployee, sendPushToAll
} from "./helpers";
import { LeaveManager } from "./Attendance";
import { Dashboard, AnnouncementBoard } from "./Dashboard";
import { EvalManager } from "./Evaluation";
import { EmployeeManager } from "./Employees";
import { PolicyPage } from "./Policy";
import { CalendarView } from "./Calendar";
import { AdminApproval } from "./Admin";
import { LunchOrder } from "./Lunch";
import { SafetyManager } from "./Safety";
import { SepManager } from "./Separation";

export default function App() {
  const MAINTENANCE = false; // 점검 시 true, 정상 운영 시 false
  const [user,setUser]=useState(null);
  const [profile,setProfile]=useState(null);
  const [authLoading,setAuthLoading]=useState(true);
  const [page,setPage]=useState(()=>{
    const ts=localStorage.getItem("hr_hidden_ts");
    const pg=localStorage.getItem("hr_page");
    if(ts && pg && Date.now()-parseInt(ts)<60*60*1000) return pg;
    return "dashboard";
  });
  const [dark,setDark]=useState(()=>localStorage.getItem("hr_dk")==="true");
  const [lang,setLang]=useState(()=>localStorage.getItem("hr_lg")||"en");
  const [sideOpen,setSO]=useState(false);
  const [notifs,setNotifs]=useState([]);
  const [showNotifs,setShowNotifs]=useState(false);
  const [isRecovery,setIsRecovery]=useState(false);
  const [refreshKey,setRefreshKey]=useState(0);
  const [isRefreshing,setIsRefreshing]=useState(false);
  const [fcmTokenCache,setFcmTokenCache]=useState(null);
  const [updateAvailable,setUpdateAvailable]=useState(false);
  useEffect(()=>{ document.body.style.background=dark?"#0f1117":"#f4f6fb"; },[dark]);


  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session) loadProfile(session.user);
      else setAuthLoading(false);
    });
    const{data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      if(event==="PASSWORD_RECOVERY"){ setIsRecovery(true); setAuthLoading(false); return; }
      if(session) loadProfile(session.user);
      else{ setUser(null); setProfile(null); setAuthLoading(false); }
    });
    return ()=>subscription.unsubscribe();
  },[]);

  async function loadProfile(u, retriesLeft=2){
    const{data,error}=await supabase.from("profiles").select("*").eq("id",u.id).single();
    if((error||!data) && retriesLeft>0){
      // 일시적인 네트워크/세션 문제일 수 있으므로, 바로 로그인 화면으로 튕기지 않고 잠깐 뒤 재시도
      await new Promise(res=>setTimeout(res,900));
      return loadProfile(u, retriesLeft-1);
    }
    setUser(u); setProfile(data||null); setAuthLoading(false);
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    if(pageParam){ window.history.replaceState({}, "", "/"); setPage(pageParam); localStorage.setItem("hr_page", pageParam); }
  }

  useEffect(()=>{
    if(!profile||profile.role!=="admin") return;
    fetchNotifs();
    const sub=supabase.channel("notifs_ch").on("postgres_changes",{event:"INSERT",schema:"public",table:"notifications"},()=>fetchNotifs()).subscribe();
    return ()=>supabase.removeChannel(sub);
  },[profile]);

  async function fetchNotifs(){
    const{data}=await supabase.from("notifications").select("*").eq("read",false).order("created_at",{ascending:false});
    if(data){ setNotifs(data); if("setAppBadge" in navigator){ if(data.length>0) navigator.setAppBadge(data.length); else navigator.clearAppBadge(); } }
  }
  async function markAllRead(){
    await supabase.from("notifications").update({read:true}).eq("read",false);
    setNotifs([]);
    if("setAppBadge" in navigator) navigator.clearAppBadge();
  }

  async function handleLogin(u,prof){
    setUser(u); setProfile(prof);
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    const lastPage = sessionStorage.getItem("last_page");
    if(pageParam){
      window.history.replaceState({}, "", "/");
      setPage(pageParam);
    } else if(lastPage){
      sessionStorage.removeItem("last_page");
      setPage(lastPage);
    } else {
      const ts=localStorage.getItem("hr_hidden_ts");
      const pg=localStorage.getItem("hr_page");
      if(ts && pg && Date.now()-parseInt(ts)<60*60*1000) setPage(pg);
      else setPage("dashboard");
    }
  }
  async function logout(){
    const fcmToken = localStorage.getItem("hr_fcm_token");
    if(fcmToken){
      try{ await supabase.from("push_subscriptions").delete().eq("fcm_token", fcmToken); }
      catch(e){ console.log("Token cleanup failed:", e); }
      localStorage.removeItem("hr_fcm_token");
    }
    localStorage.removeItem("hr_page");
    localStorage.removeItem("hr_hidden_ts");
    sessionStorage.removeItem("last_page");
    await supabase.auth.signOut();
  }


  const sideOpenRef=useRef(false);
  useEffect(()=>{ sideOpenRef.current=sideOpen; },[sideOpen]);

  // 안드로이드 뒤로가기/스와이프가 앱을 끄지 않고 이전 페이지로 이동하도록 처리
  useEffect(()=>{
    if(!user||!profile||!profile.approved) return;
    window.history.replaceState({page:page}, "", window.location.pathname);
    function onPopState(e){
      if(sideOpenRef.current){ setSO(false); return; }
      if(e.state&&e.state.page){
        setPage(e.state.page);
        localStorage.setItem("hr_page", e.state.page);
        setPageStack(s=>{
          const idx=s.lastIndexOf(e.state.page);
          return idx>=0?s.slice(0,idx+1):[...s,e.state.page];
        });
      }
    }
    window.addEventListener("popstate", onPopState);
    return ()=>window.removeEventListener("popstate", onPopState);
  },[user, profile?.approved]);

  const [pageStack,setPageStack]=useState(["dashboard"]);

  function goPage(p){
    localStorage.setItem("hr_page",p);
    setPage(p);
    setSO(false);
    window.history.pushState({page:p}, "", window.location.pathname);
    setPageStack(s=>s[s.length-1]===p?s:[...s,p]);
  }
  function goBack(){
    setPageStack(s=>{
      if(s.length<=1) return s;
      const next=s.slice(0,-1);
      const prev=next[next.length-1];
      setPage(prev);
      localStorage.setItem("hr_page",prev);
      return next;
    });
  }
  function goDark(v){ localStorage.setItem("hr_dk",v); setDark(v); document.body.style.background=v?"#0f1117":"#f4f6fb"; }
  function goLang(v){ 
    localStorage.setItem("hr_lg",v); 
    setLang(v);
    if(user) supabase.from("push_subscriptions").update({ lang: v }).eq("user_id", user.id).then(()=>{});
  }

  // 푸시 알림 구독 (FCM 방식)
useEffect(()=>{
    if(!user || !profile) return;
    if(!("serviceWorker" in navigator)) return;
    (async()=>{
      try{
        if(!("Notification" in window)) return;
        if(Notification.permission === "denied") return;
        const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
        if(permission !== "granted") return;

        const reg = await navigator.serviceWorker.ready;
        const messaging = getMessaging(firebaseApp);
        const fcmToken = await getToken(messaging, {
          vapidKey: FCM_VAPID_KEY,
          serviceWorkerRegistration: reg
        });
        if(fcmToken) await saveFcmToken(fcmToken);
      }catch(e){ console.log("FCM token registration failed:", e); }
    })();
  },[user?.id, profile?.role]);

  async function saveFcmToken(token){
  localStorage.setItem("hr_fcm_token", token);
  const { data: existing } = await supabase.from("push_subscriptions").select("id").eq("fcm_token", token).maybeSingle();
  if(existing){
    await supabase.from("push_subscriptions").update({ user_id: user.id, lang: lang }).eq("fcm_token", token);
  } else {
    await supabase.from("push_subscriptions").insert([{ user_id: user.id, fcm_token: token, lang: lang }]);
  }
}

  // SW 메시지 수신 (알림 클릭 시 페이지 이동)
  useEffect(()=>{
    const onMessage=(e)=>{
      if(e.data?.type==="NAVIGATE"){
        const pg=e.data.url || "dashboard";
        goPage(pg);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return()=>{ navigator.serviceWorker?.removeEventListener("message", onMessage); };
  },[]);

  // 새 버전 배포 감지: 대기 중인 새 SW가 있으면 배너를 띄우고,
  // 사용자가 직접 탭했을 때만 전환합니다 (자동 새로고침 루프 방지).
  useEffect(()=>{
    if(!("serviceWorker" in navigator)) return;
    let reloaded=false;
    const onControllerChange=()=>{
      if(reloaded) return;
      reloaded=true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    navigator.serviceWorker.getRegistration().then(reg=>{
      if(!reg) return;
      if(reg.waiting) setUpdateAvailable(true);
      reg.addEventListener("updatefound", ()=>{
        const newWorker=reg.installing;
        if(!newWorker) return;
        newWorker.addEventListener("statechange", ()=>{
          if(newWorker.state==="installed" && navigator.serviceWorker.controller){
            setUpdateAvailable(true);
          }
        });
      });
    }).catch(()=>{});

    // 앱이 열려 있거나 백그라운드에 있어도 새 배포를 감지: 서비스워커 업데이트 확인 + 실행 중인 번들과 서버 번들 비교
    const runningJs=([...document.scripts].map(x=>x.src).find(u=>/\/static\/js\/main\.[a-z0-9]+\.js/.test(u))||"").split("/").pop();
    const checkUpdate=async()=>{
      try{
        const reg=await navigator.serviceWorker.getRegistration();
        reg?.update().catch(()=>{});
        if(!runningJs) return;
        const html=await (await fetch("/",{cache:"no-store"})).text();
        const latest=(html.match(/static\/js\/(main\.[a-z0-9]+\.js)/)||[])[1];
        if(latest && latest!==runningJs) setUpdateAvailable(true);
      }catch(e){}
    };
    const onVis=()=>{ if(document.visibilityState==="visible") checkUpdate(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", checkUpdate);
    const iv=setInterval(checkUpdate, 5*60*1000);
    checkUpdate();

    return()=>{
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", checkUpdate);
      clearInterval(iv);
    };
  },[]);

  function applyUpdate(){
    setUpdateAvailable(false);
    navigator.serviceWorker.getRegistration().then(reg=>{
      if(reg?.waiting){
        reg.waiting.postMessage({type:"SKIP_WAITING"});
        setTimeout(()=>window.location.reload(), 1500); // controllerchange가 안 와도 새로고침
      } else {
        window.location.reload();
      }
    });
  }

  // 포그라운드 복귀 시 자동 새로고침 (1시간 이상 지났을 때만)
  useEffect(()=>{
    const onVisibilityChange=()=>{
      if(document.visibilityState==="hidden"){
        localStorage.setItem("hr_hidden_ts", Date.now().toString());
      } else if(document.visibilityState==="visible"){
        const ts=localStorage.getItem("hr_hidden_ts");
        const hasPageParam = new URLSearchParams(window.location.search).get("page");
        if(!hasPageParam && ts && Date.now()-parseInt(ts)>60*60*1000) window.location.reload();
      }
    };
    document.addEventListener("visibilitychange",onVisibilityChange);
    return()=>document.removeEventListener("visibilitychange",onVisibilityChange);
  },[]);
  // 화면 왼쪽 끝에서 오른쪽으로 스와이프 → 앱 내 이전 페이지로 이동 (iOS 대응)
  useEffect(()=>{
    let startX=0, startY=0, edgeSwipe=false;
    const onTouchStart=(e)=>{
      startX=e.touches[0].clientX;
      startY=e.touches[0].clientY;
      edgeSwipe=startX<24;
    };
    const onTouchEnd=(e)=>{
      if(!edgeSwipe) return;
      const distX=e.changedTouches[0].clientX-startX;
      const distY=Math.abs(e.changedTouches[0].clientY-startY);
      if(distX>70&&distY<60) goBack();
    };
    window.addEventListener("touchstart",onTouchStart,{passive:true});
    window.addEventListener("touchend",onTouchEnd,{passive:true});
    return()=>{ window.removeEventListener("touchstart",onTouchStart); window.removeEventListener("touchend",onTouchEnd); };
  },[pageStack]);
  // Pull to Refresh (iOS PWA)
  useEffect(()=>{
    let startY=0, pulling=false;
    const onTouchStart=(e)=>{ startY=e.touches[0].clientY; pulling=window.scrollY===0; };
    const onTouchEnd=(e)=>{
      if(!pulling) return;
      const dist=e.changedTouches[0].clientY-startY;
      if(dist>80){
        setIsRefreshing(true);
        setRefreshKey(k=>k+1);
        setTimeout(()=>setIsRefreshing(false),1000);
      }
    };
    window.addEventListener("touchstart",onTouchStart,{passive:true});
    window.addEventListener("touchend",onTouchEnd,{passive:true});
    return()=>{ window.removeEventListener("touchstart",onTouchStart); window.removeEventListener("touchend",onTouchEnd); };
  },[page]);

  const t=lang==="kr"?KO:lang==="es"?ES:EN;
  const isAdmin=profile?.role==="admin";

  if(MAINTENANCE) return(
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:dark?"#0f1117":"#f1f5f9",fontFamily:"'Noto Sans KR',sans-serif",padding:20}}>
      <Wrench size={48} color="#f59e0b" style={{marginBottom:20}}/>
      <div style={{fontSize:22,fontWeight:800,color:dark?"#e2e8f0":"#1e3a5f",marginBottom:12}}>
        {lang==="kr"?"시스템 점검 중":lang==="es"?"Sistema en mantenimiento":"System Maintenance"}
      </div>
      <div style={{fontSize:15,color:dark?"#94a3b8":"#64748b",textAlign:"center",lineHeight:1.7,whiteSpace:"pre-line"}}>
        {lang==="kr"
          ?"시스템 점검 중입니다.\n잠시 후 다시 이용해 주세요."
          :lang==="es"
          ?"Sistema en mantenimiento.\nIntenta de nuevo más tarde."
          :"System maintenance in progress.\nPlease try again later."}
      </div>
    </div>
  );

  if(authLoading) return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:dark?"#0f1117":"#f1f5f9"}}>
      <div style={{fontSize:15,color:dark?"#94a3b8":"#64748b",fontWeight:600}}>{lang==="kr"?"불러오는 중...":lang==="es"?"Cargando...":"Loading..."}</div>
    </div>
  );

  if(isRecovery) return <ResetPassword lang={lang} dark={dark} onDone={()=>{ setIsRecovery(false); supabase.auth.signOut(); }}/>;

  if(!user||!profile) return <Login t={t} lang={lang} setLang={goLang} dark={dark} setDark={goDark} onLogin={handleLogin}/>;

  if(!profile.approved) return(
    <PendingApproval lang={lang} dark={dark} onLogout={logout} userId={user?.id} onApproved={()=>{
      supabase.auth.signOut();
    }}/>
  );

  const adminNav=[
    {id:"dashboard",icon:<LayoutDashboard size={20}/>,label:t.nav.dash},
    {id:"leave",icon:<Clock size={20}/>,label:t.nav.leave},
    {id:"calendar",icon:<Calendar size={20}/>,label:t.nav.cal},
    {id:"safety",icon:<Shield size={20}/>,label:t.nav.safety},
    {id:"eval",icon:<Star size={20}/>,label:t.nav.eval},
    {id:"employees",icon:<Users size={20}/>,label:t.nav.emp},
    {id:"sep",icon:<FolderOpen size={20}/>,label:t.nav.sep},
    {id:"admin",icon:<Lock size={20}/>,label:lang==="kr"?"직원 승인":lang==="es"?"Aprobaciones":"Approvals"},
    {id:"lunch",icon:<UtensilsCrossed size={20}/>,label:t.nav.lunch},
  ];
  const empNav=[
    {id:"dashboard",icon:<LayoutDashboard size={20}/>,label:t.nav.dash},
    {id:"leave",icon:<Clock size={20}/>,label:t.nav.leave},
    {id:"calendar",icon:<Calendar size={20}/>,label:t.nav.cal},
    {id:"eval",icon:<Star size={20}/>,label:t.nav.eval},
    {id:"lunch",icon:<UtensilsCrossed size={20}/>,label:t.nav.lunch},
  ];
  const bNav=isAdmin?adminNav:empNav;

  return(
    <><style>{getStyle(dark)}</style>
    {isRefreshing&&(
      <div style={{position:"fixed",top:0,left:0,right:0,height:3,zIndex:9999,background:"var(--ac)",animation:"refreshBar 1s ease-out forwards"}}/>
    )}
    {updateAvailable&&(
      <div onClick={applyUpdate} style={{position:"fixed",top:0,left:0,right:0,zIndex:10000,background:"var(--ac)",color:dark?"#0f1117":"#fff",padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"center",gap:10,cursor:"pointer",fontSize:13,fontWeight:600,boxShadow:"0 2px 8px rgba(0,0,0,.2)"}}>
        <RefreshCw size={15}/>
        {lang==="kr"?"새 업데이트가 있습니다. 탭하여 업데이트":lang==="es"?"Hay una actualización nueva. Toca para actualizar":"A new update is available. Tap to update"}
      </div>
    )}
    <div className="app">
      <div className={`overlay ${sideOpen?"open":""}`} onClick={()=>setSO(false)}/>
      <div className="topbar">
        <button className="hbg" style={{marginLeft:-8}} onClick={()=>setSO(!sideOpen)}><Menu size={22}/></button>
        <div className="tbt" onClick={()=>goPage("dashboard")} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><img src={dark?"/logo-symbol-white.png":"/logo-symbol-navy.png"} alt="" style={{width:22,height:22,display:"block",flexShrink:0}}/><span style={{fontFamily:"'Quantico',sans-serif",fontWeight:700,fontSize:15,color:dark?"#fff":"#1e3a5f",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>SPRINGBLINDS TEAM</span></div>
        <div style={{display:"flex",alignItems:"center",gap:2,flexShrink:0,height:36}}>
          {isAdmin&&(
            <div style={{position:"relative"}}>
              <button onClick={()=>setShowNotifs(!showNotifs)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,position:"relative",padding:0,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Bell size={17}/>{notifs.length>0&&<span style={{position:"absolute",top:2,right:2,background:"var(--re)",color:"#fff",borderRadius:"50%",fontSize:9,fontWeight:700,width:14,height:14,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>{notifs.length}</span>}
              </button>
              {showNotifs&&(
                <div className="notif-pop" style={{position:"absolute",right:0,top:"110%",background:"var(--sf)",border:"1px solid var(--bd)",borderRadius:12,boxShadow:"0 8px 24px rgba(0,0,0,.15)",width:300,zIndex:9999}} onClick={e=>e.stopPropagation()}>
                  <div style={{padding:"12px 16px",borderBottom:"1px solid var(--bd)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontWeight:700,fontSize:13}}>{lang==="kr"?"알림":lang==="es"?"Notificaciones":"Notifications"}</span>
                    {notifs.length>0&&<button onClick={markAllRead} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"var(--ac)",fontWeight:600}}>{lang==="kr"?"모두 읽음":lang==="es"?"Marcar todo como leído":"Mark all read"}</button>}
                  </div>
                  <div style={{maxHeight:280,overflowY:"auto"}}>
                    {notifs.length===0
                      ?<div style={{padding:16,textAlign:"center",fontSize:13,color:"var(--tx3)"}}>{lang==="kr"?"새 알림 없음":lang==="es"?"Sin notificaciones nuevas":"No notifications"}</div>
                      :notifs.map(n=>(
                        <div key={n.id} style={{padding:"10px 16px",borderBottom:"1px solid var(--bd)",fontSize:13}}>
                          <div style={{fontWeight:600,marginBottom:2}}>{n.message}</div>
                          <div style={{fontSize:11,color:"var(--tx3)"}}>{new Date(n.created_at).toLocaleString()}</div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          )}
          {!isAdmin&&<span className="tb-user" style={{fontSize:12,color:"var(--tx3)",fontWeight:600,alignItems:"center"}}><User size={13} style={{marginRight:4}}/>{profile.emp_name}</span>}
          <div style={{transform:"translateY(-2px)"}}><LangSwitch lang={lang} setLang={goLang}/></div>
        </div>
      </div>
      <Sidebar t={t} page={page} setPage={goPage} dark={dark} setDark={goDark} lang={lang} setLang={goLang} onLogout={logout} isOpen={sideOpen} isAdmin={isAdmin}/>
      <div className="main" onClick={()=>setShowNotifs(false)}>

        {page==="dashboard"  && <Dashboard t={t} lang={lang} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey} goPage={goPage}/>}
        {page==="announcements" && <AnnouncementBoard t={t} lang={lang} isAdmin={isAdmin}/>}
        {page==="leave"      && <LeaveManager key={refreshKey} t={t} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey}/>}
        {page==="calendar"   && <CalendarView key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey}/>}
        {page==="eval"       && <EvalManager key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey}/>}
        {isAdmin&&page==="employees" && <EmployeeManager key={refreshKey} t={t} lang={lang} refreshKey={refreshKey}/>}
        {page==="safety"    && <SafetyManager key={refreshKey} t={t} lang={lang} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey}/>}
        {isAdmin&&page==="sep"       && <SepManager key={refreshKey} t={t} lang={lang} refreshKey={refreshKey}/>}
        {page==="policy"    && <PolicyPage t={t} lang={lang} isAdmin={isAdmin}/>}
        {isAdmin&&page==="admin"     && <AdminApproval key={refreshKey} lang={lang} refreshKey={refreshKey}/>}
        {page==="lunch"      && <LunchOrder lang={lang} isAdmin={isAdmin} empName={profile.emp_name} refreshKey={refreshKey}/>}
      </div>
      <div className="bnav">
        {bNav.map(n=>(
          <div key={n.id} className={`bni ${page===n.id?"active":""}`} onClick={()=>goPage(n.id)}>
            <span className="bni-icon">{n.icon}</span><span>{n.label}</span>
          </div>
        ))}
      </div>
      <a href="https://chat.whatsapp.com/Hgyy9GFeBhfHln48It3eqX" target="_blank" rel="noopener noreferrer"
        style={{
          position:"fixed", bottom:80, right:20, zIndex:9999,
          width:56, height:56, borderRadius:"50%",
          display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 4px 14px rgba(0,0,0,.25)", textDecoration:"none"
        }}>
        <img src="/whatsapp-icon.png" alt="WhatsApp" style={{width:"100%", height:"100%", objectFit:"contain"}}/>
      </a>
    </div></>
  );
}


function PendingApproval({lang, dark, onLogout, userId, onApproved}){
  const KO=lang==="kr";
  const SP=lang==="es";
  const [approved,setApproved]=useState(false);
  const bg=dark?"#0f1117":"#f1f5f9";
  const card=dark?"#1a1d27":"#ffffff";
  const tx=dark?"#e2e8f0":"#1e293b";
  const tx2=dark?"#94a3b8":"#64748b";
  const tx3=dark?"#64748b":"#94a3b8";
  const bd=dark?"1px solid #2e3352":"0.5px solid #dde2ee";

  useEffect(()=>{
    const interval=setInterval(async()=>{
      const{data}=await supabase.from("profiles").select("approved").eq("id",userId).single();
      if(data?.approved){
        setApproved(true);
        clearInterval(interval);
        setTimeout(()=>{ onApproved(); },3000);
      }
    },5000);
    return()=>clearInterval(interval);
  },[userId]);

  const StepIcon=({done, waiting})=>{
    if(done) return(
      <div style={{width:22,height:22,borderRadius:"50%",background:"#EAF3DE",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Check size={12} color="#3B6D11" strokeWidth={2.5}/>
      </div>
    );
    if(waiting) return(
      <div style={{width:22,height:22,borderRadius:"50%",background:dark?"#1e3a5f":"#E6F1FB",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <Clock size={12} color={dark?"#6ea8e0":"#185FA5"} strokeWidth={2}/>
      </div>
    );
    return(
      <div style={{width:22,height:22,borderRadius:"50%",border:bd,flexShrink:0}}/>
    );
  };

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:20}}>
      <div style={{background:card,borderRadius:16,padding:32,maxWidth:360,width:"100%",textAlign:"center",border:bd}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:approved?"#EAF3DE":dark?"#1e3a5f":"#E6F1FB",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px"}}>
          {approved
            ? <Check size={24} color="#3B6D11" strokeWidth={2}/>
            : <Clock size={24} color={dark?"#6ea8e0":"#185FA5"} strokeWidth={2}/>
          }
        </div>
        <div style={{fontWeight:700,fontSize:18,marginBottom:8,color:tx}}>
          {approved?(KO?"승인 완료!":SP?"¡Aprobado!":"You're approved!"):(KO?"가입 완료!":SP?"¡Registro completo!":"Sign up complete!")}
        </div>
        <div style={{fontSize:13,color:tx2,marginBottom:6,lineHeight:1.6}}>
          {approved
            ?(KO?"승인이 완료되었습니다. 잠시 후 로그인 화면으로 이동합니다.":SP?"¡Fuiste aprobado! Redirigiendo al inicio de sesión...":"You've been approved! Redirecting to login...")
            :(KO?"SPRINGBLINDS TEAM에 가입해주셔서 감사합니다. 관리자 승인 후 이용 가능합니다.":SP?"Gracias por unirte a SPRINGBLINDS TEAM. Espera la aprobación del administrador.":"Thanks for joining SPRINGBLINDS TEAM. Please wait for admin approval.")
          }
        </div>
        {!approved&&<div style={{fontSize:12,color:tx3,marginBottom:24}}>{KO?"승인이 완료되면 자동으로 알려드립니다.":SP?"Te avisaremos cuando sea aprobado.":"You'll be notified once approved."}</div>}
        {approved&&<div style={{fontSize:12,color:"#3B6D11",marginBottom:24}}>{KO?"3초 후 자동으로 이동합니다...":SP?"Redirigiendo en 3 segundos...":"Redirecting in 3 seconds..."}</div>}

        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:24,textAlign:"left"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <StepIcon done={true}/>
            <span style={{fontSize:13,color:tx}}>{KO?"계정 생성 완료":SP?"Cuenta creada":"Account created"}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <StepIcon done={approved} waiting={!approved}/>
            <span style={{fontSize:13,color:tx}}>{KO?"승인 대기 중":SP?"Esperando aprobación":"Waiting for approval"}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <StepIcon done={approved} waiting={false}/>
            <span style={{fontSize:13,color:approved?tx:tx3}}>{KO?"이용 가능":SP?"Acceso concedido":"Access granted"}</span>
          </div>
        </div>

        {!approved&&(
          <button onClick={onLogout} style={{width:"100%",padding:"10px",borderRadius:8,border:bd,background:"transparent",color:tx,fontSize:14,cursor:"pointer",fontWeight:600}}>
            {KO?"로그아웃":SP?"Cerrar sesión":"Log out"}
          </button>
        )}
      </div>
    </div>
  );
}

function ResetPassword({lang,onDone,dark}){
  const [pw,setPw]=useState("");
  const [pw2,setPw2]=useState("");
  const [msg,setMsg]=useState("");
  const [loading,setLoading]=useState(false);
  const [done,setDone]=useState(false);
  const inp={width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:14,outline:"none",boxSizing:"border-box"};

  async function handleSubmit(){
    if(pw.length<6){ setMsg(lang==="kr"?"비밀번호는 6자 이상이어야 합니다.":lang==="es"?"La contraseña debe tener al menos 6 caracteres.":"Password must be at least 6 characters."); return; }
    if(pw!==pw2){ setMsg(lang==="kr"?"비밀번호가 일치하지 않습니다.":lang==="es"?"Las contraseñas no coinciden.":"Passwords do not match."); return; }
    setLoading(true); setMsg("");
    const{error}=await supabase.auth.updateUser({password:pw});
    if(error){ setMsg(error.message); setLoading(false); return; }
    setDone(true); setLoading(false);
    setTimeout(()=>onDone(),2000);
  }

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:dark?"#0f1117":"#f1f5f9",padding:20}}>
      <div style={{background:dark?"#1a1d27":"#fff",borderRadius:16,padding:32,maxWidth:380,width:"100%",boxShadow:"0 4px 24px rgba(0,0,0,.1)",border:dark?"1px solid #2e3352":"none"}}>
        <div style={{fontWeight:700,fontSize:20,marginBottom:8,textAlign:"center",color:dark?"#e2e8f0":"#1e293b"}}>SPRINGBLINDS TEAM</div>
        <div style={{fontSize:14,color:dark?"#94a3b8":"#64748b",marginBottom:24,textAlign:"center"}}>{lang==="kr"?"새 비밀번호 설정":lang==="es"?"Establecer nueva contraseña":"Set New Password"}</div>
        {done?(
          <div style={{textAlign:"center",color:"#059669",fontWeight:600,fontSize:15}}>{lang==="kr"?"비밀번호가 변경되었습니다! 다시 로그인해주세요.":lang==="es"?"¡Contraseña actualizada! Inicia sesión de nuevo.":"Password updated! Please log in again."}</div>
        ):(
          <>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:12,color:dark?"#94a3b8":"#64748b",marginBottom:4}}>{lang==="kr"?"새 비밀번호":lang==="es"?"Nueva contraseña":"New Password"}</div>
              <input type="password" value={pw} onChange={e=>setPw(e.target.value)} style={inp} minLength={6} placeholder={lang==="kr"?"6자 이상":lang==="es"?"Al menos 6 caracteres":"At least 6 characters"}/>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,color:dark?"#94a3b8":"#64748b",marginBottom:4}}>{lang==="kr"?"비밀번호 확인":lang==="es"?"Confirmar contraseña":"Confirm Password"}</div>
              <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} style={inp} placeholder={lang==="kr"?"비밀번호 재입력":lang==="es"?"Repite la contraseña":"Re-enter password"}/>
            </div>
            {msg&&<div style={{fontSize:13,color:"#ef4444",marginBottom:12}}>{msg}</div>}
            <button onClick={handleSubmit} disabled={loading} style={{width:"100%",padding:"11px",borderRadius:8,border:"none",background:"#1e3a5f",color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer"}}>
              {loading?(lang==="kr"?"변경 중...":lang==="es"?"Actualizando...":"Updating..."):(lang==="kr"?"비밀번호 변경":lang==="es"?"Cambiar contraseña":"Update Password")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Login({t,lang,setLang,dark,setDark,onLogin}){
  const [mode,setMode]=useState("login");
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [empName,setEmpName]=useState("");
  const [birthday,setBirthday]=useState("");
  const [phone,setPhone]=useState("");
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState("");
  const [msgOk,setMsgOk]=useState(false);

  async function handleReset(e){
    e.preventDefault(); setLoading(true); setMsg(""); setMsgOk(false);
    const{error}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:window.location.origin});
    if(error){ setMsg(lang==="kr"?"이메일 전송 실패. 이메일을 확인해주세요.":lang==="es"?"No se pudo enviar. Revisa tu correo.":"Failed to send. Check your email."); }
    else{ setMsgOk(true); setMsg(lang==="kr"?"비밀번호 재설정 링크를 이메일로 발송했습니다.":lang==="es"?"Enviamos el enlace para restablecer la contraseña a tu correo.":"Password reset link sent to your email."); }
    setLoading(false);
  }

  async function handleLogin(e){
    e.preventDefault(); setLoading(true); setMsg(""); setMsgOk(false);
    const{data,error}=await supabase.auth.signInWithPassword({email,password:pw});
    if(error){ setMsg(lang==="kr"?"이메일 또는 비밀번호가 올바르지 않습니다.":lang==="es"?"Correo o contraseña incorrectos.":"Invalid email or password."); setLoading(false); return; }
    const{data:prof,error:pe}=await supabase.from("profiles").select("approved,role,emp_name").eq("id",data.user.id).single();
    if(pe||!prof){
      await supabase.auth.signOut();
      setMsg(lang==="kr"?"프로필을 찾을 수 없습니다. 관리자에게 문의하세요.":lang==="es"?"Perfil no encontrado. Contacta al administrador.":"Profile not found. Contact admin."); setLoading(false); return;
    }
    // 관리자는 승인 여부 관계없이 로그인 허용
    if(prof.role!=="admin"&&!prof.approved){
      await supabase.auth.signOut();
      setMsg(lang==="kr"?"관리자 승인 대기 중입니다.":lang==="es"?"Pendiente de aprobación del administrador.":"Pending admin approval."); setLoading(false); return;
    }
    onLogin(data.user, prof); setLoading(false);
  }

  async function handleSignup(e){
    e.preventDefault();
    if(!empName.trim()){ setMsg(lang==="kr"?"이름을 입력해주세요.":lang==="es"?"Ingresa tu nombre.":"Please enter your name."); return; }
    setLoading(true); setMsg(""); setMsgOk(false);
    const{data,error}=await supabase.auth.signUp({email,password:pw});
    if(error){ setMsg(error.message); setLoading(false); return; }
    await supabase.from("profiles").insert([{id:data.user.id,emp_name:empName.trim(),role:"employee",approved:false,birthday:birthday||null,phone:phone.trim()||null}]);
    // 회원가입 시 Employees에도 Active 상태로 자동 추가 (이메일 포함)
    const{data:existing}=await supabase.from("employees").select("id,email").eq("name",empName.trim()).single();
    if(!existing){
      await supabase.from("employees").insert([{name:empName.trim(),birthday:birthday||null,phone:phone.trim()||null,status:"Active",position:"",dept:"",email:email.trim()}]);
    } else if(!existing.email){
      await supabase.from("employees").update({email:email.trim()}).eq("id",existing.id);
    }
    await sendPushToAdmin(supabase, "신규 가입 승인 요청", `${empName.trim()}님이 가입을 신청했습니다. 승인이 필요합니다.`, "New Signup Request", `${empName.trim()} has requested to join. Approval needed.`, "/admin", {title:"Nueva solicitud de registro", body:`${empName.trim()} solicitó unirse. Se requiere aprobación.`});
    setMsgOk(true);
    setMsg(lang==="kr"?"가입 신청 완료! 관리자 승인 후 로그인 가능합니다.":lang==="es"?"¡Registro completo! Podrás iniciar sesión cuando el administrador te apruebe.":"Sign up complete! You can log in after admin approval.");
    setMode("login"); setLoading(false);
  }

  // 무채색 컨셉
  const bg   = dark ? "#0f1117" : "#f4f6fb";
  const card = dark ? "#1a1d27" : "#ffffff";
  const tx   = dark ? "#e2e8f0" : "#1e293b";
  const tx3  = dark ? "#64748b" : "#94a3b8";
  const bd   = dark ? "#2e3352" : "#dde2ee";
  const sf2  = dark ? "#22263a" : "#f0f2f8";
  const tabBg= dark ? "#22263a" : "#efefef";
  const ac   = dark ? "#e2e8f0" : "#1e3a5f";
  const acTx = dark ? "#0f1117" : "#ffffff";

  const inp={width:"100%",padding:"10px 14px",borderRadius:6,border:`1px solid ${dark?"transparent":bd}`,background:sf2,color:tx,fontSize:14,boxSizing:"border-box",outline:"none"};

  return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:bg,padding:20,fontFamily:"'Noto Sans KR',sans-serif"}}>
      <div style={{background:card,borderRadius:12,padding:"40px 36px",width:"100%",maxWidth:400,boxShadow:"0 2px 20px rgba(0,0,0,.08)"}}>
        <div style={{textAlign:"center",paddingTop:10,marginBottom:22}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9}}><img src={dark?"/logo-symbol-white.png":"/logo-symbol-navy.png"} alt="" style={{width:34,height:34,display:"block"}}/><span style={{fontFamily:"'Quantico',sans-serif",fontWeight:700,fontSize:30,lineHeight:1,color:dark?"#fff":"#1e3a5f"}}>SPRINGBLINDS</span></div>
          <div style={{fontSize:13,color:tx3,marginTop:8,letterSpacing:1,textTransform:"uppercase"}}>TEAM</div>
        </div>

        {mode==="reset"?(
          <div style={{marginBottom:24}}>
            <button type="button" onClick={()=>{setMode("login");setMsg("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:tx3,padding:0,display:"flex",alignItems:"center",gap:4}}>
              ← {lang==="kr"?"로그인으로 돌아가기":lang==="es"?"Volver al inicio de sesión":"Back to login"}
            </button>
            <div style={{fontSize:18,fontWeight:800,color:tx,marginTop:8}}>{lang==="kr"?"비밀번호 찾기":lang==="es"?"Restablecer contraseña":"Reset Password"}</div>
            <div style={{fontSize:12,color:tx3,marginTop:4}}>{lang==="kr"?"가입한 이메일을 입력하면 재설정 링크를 보내드립니다.":lang==="es"?"Ingresa tu correo para recibir un enlace de restablecimiento.":"Enter your email to receive a reset link."}</div>
          </div>
        ):(
        <div style={{display:"flex",gap:2,marginBottom:28,background:tabBg,borderRadius:8,padding:3}}>
          {["login","signup"].map(m=>(
            <button key={m} onClick={()=>{setMode(m);setMsg("");}} style={{
              flex:1,padding:"9px 0",borderRadius:6,border:"none",cursor:"pointer",
              fontWeight:700,fontSize:13,transition:"all .15s",letterSpacing:.3,
              background:mode===m?ac:"transparent",
              color:mode===m?acTx:tx3
            }}>
              {m==="login"?(lang==="kr"?"로그인":lang==="es"?"Iniciar sesión":"Login"):(lang==="kr"?"회원가입":lang==="es"?"Registrarse":"Sign Up")}
            </button>
          ))}
        </div>
        )}

        <form onSubmit={mode==="login"?handleLogin:mode==="reset"?handleReset:handleSignup}>
          {mode==="signup"&&(
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:700,color:tx3,display:"block",marginBottom:6,letterSpacing:.5,textTransform:"uppercase"}}>
                {lang==="kr"?"이름":lang==="es"?"Nombre completo":"Full Name"}
              </label>
              <input value={empName} onChange={e=>setEmpName(e.target.value)} required style={inp}
                placeholder={lang==="kr"?"예: Gildong Hong":lang==="es"?"Ej.: Gildong Hong":"e.g. Gildong Hong"}/>
            </div>
          )}
          <div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:tx3,display:"block",marginBottom:6,letterSpacing:.5,textTransform:"uppercase"}}>{lang==="kr"?"이메일":lang==="es"?"Correo electrónico":"Email"}</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required style={inp}
              placeholder="email@example.com"/>
          </div>
          {mode!=="reset"&&<div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:tx3,display:"block",marginBottom:6,letterSpacing:.5,textTransform:"uppercase"}}>
              {lang==="kr"?"비밀번호":lang==="es"?"Contraseña":"Password"}
            </label>
            <input type="password" value={pw} onChange={e=>setPw(e.target.value)} required={mode!=="reset"} minLength={6} style={inp}
              placeholder={lang==="kr"?"6자 이상":lang==="es"?"Al menos 6 caracteres":"At least 6 characters"}/>
          </div>}
          {mode==="signup"&&(
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:700,color:tx3,display:"block",marginBottom:6,letterSpacing:.5,textTransform:"uppercase"}}>
                {lang==="kr"?"연락처":lang==="es"?"Teléfono":"Phone Number"}
              </label>
              <input type="tel" value={phone} onChange={e=>{
                const digits=e.target.value.replace(/\D/g,"").slice(0,10);
                const formatted=digits.length<=3?digits:digits.length<=6?`${digits.slice(0,3)}-${digits.slice(3)}`:`${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
                setPhone(formatted);
              }} required style={inp} placeholder={lang==="kr"?"예: 201-123-4567":lang==="es"?"ej. 201-123-4567":"e.g. 201-123-4567"}/>
            </div>
          )}
          {mode==="signup"&&(
            <div style={{marginBottom:24}}>
              <label style={{fontSize:11,fontWeight:700,color:tx3,display:"block",marginBottom:6,letterSpacing:.5,textTransform:"uppercase"}}>
                {lang==="kr"?"생년월일":lang==="es"?"Fecha de nacimiento":"Date of Birth"}
              </label>
              <input type="text" value={birthday} onChange={e=>{
                const digits=e.target.value.replace(/\D/g,"").slice(0,8);
                const formatted=digits.length<=2?digits:digits.length<=4?`${digits.slice(0,2)}/${digits.slice(2)}`:`${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
                setBirthday(formatted);
              }} required style={inp} placeholder="MM/DD/YYYY"/>
            </div>
          )}
          {msg&&(
            <div style={{marginBottom:16,padding:"10px 14px",borderRadius:6,fontSize:13,
              background:msgOk?"rgba(0,0,0,.04)":"rgba(0,0,0,.04)",
              color:msgOk?"#2d6a4f":"#c0392b",
              border:`1px solid ${msgOk?"#b7d1c1":"#e8b4b0"}`}}>
              {msg}
            </div>
          )}
          <button type="submit" disabled={loading} style={{
            width:"100%",padding:"13px 0",borderRadius:6,border:"none",
            background:ac,color:acTx,fontWeight:700,fontSize:14,letterSpacing:.5,
            cursor:loading?"not-allowed":"pointer",opacity:loading?.6:1,textTransform:"uppercase"
          }}>
            {loading?"...":(mode==="login"?(lang==="kr"?"로그인":lang==="es"?"Iniciar sesión":"Login"):mode==="reset"?(lang==="kr"?"링크 발송":lang==="es"?"Enviar enlace":"Send Link"):(lang==="kr"?"가입 신청":lang==="es"?"Registrarse":"Sign Up"))}
          </button>
          {mode==="login"&&(
            <div style={{textAlign:"center",marginTop:14}}>
              <button type="button" onClick={()=>{setMode("reset");setMsg("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:tx3,textDecoration:"underline"}}>
                {lang==="kr"?"비밀번호 찾기":lang==="es"?"¿Olvidaste tu contraseña?":"Forgot password?"}
              </button>
            </div>
          )}
        </form>

        <div style={{marginTop:24,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <button onClick={()=>setDark(!dark)} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:tx3}}>
            {dark?<Sun size={18}/>:<Moon size={18}/>}
          </button>
          <LangSwitch lang={lang} setLang={setLang}/>
        </div>
        <div style={{marginTop:22,paddingTop:16,borderTop:`1px solid ${bd}`}}>
          <AppFooter color={tx3} size={11}/>
          <div style={{textAlign:"center",fontSize:10,color:tx3,opacity:.6,marginTop:6}}>build {BUILD_ID}</div>
        </div>
      </div>
    </div>
  );
}


function Sidebar({t,page,setPage,dark,setDark,lang,setLang,onLogout,isOpen,pwHint,saveHint,isAdmin}){
  const [editHint,setEditHint]=useState(false);
  const [hintVal,setHintVal]=useState(pwHint||"SB + 4자리 숫자");
  const allNav=[
    {id:"dashboard",icon:<LayoutDashboard size={17}/>,label:t.nav.dash},
    {id:"leave",icon:<Clock size={17}/>,label:t.nav.leave},
    {id:"calendar",icon:<Calendar size={17}/>,label:t.nav.cal},
    {id:"lunch",icon:<UtensilsCrossed size={17}/>,label:t.nav.lunch},
    {id:"eval",icon:<Star size={17}/>,label:t.nav.eval},
    {id:"safety",icon:<Shield size={17}/>,label:t.nav.safety},
    {id:"employees",icon:<Users size={17}/>,label:t.nav.emp,adminOnly:true},
    {id:"sep",icon:<FolderOpen size={17}/>,label:t.nav.sep,adminOnly:true},
    {id:"policy",icon:<FileText size={17}/>,label:t.nav.policy},
    {id:"admin",icon:<Lock size={17}/>,label:lang==="kr"?"직원 승인":lang==="es"?"Aprobaciones":"Approvals",adminOnly:true},
  ];
  const nav=allNav.filter(n=>isAdmin||!n.adminOnly);
  return(
    <div className={`sidebar ${isOpen?"open":""}`}>
      <div className="sbl" onClick={()=>{setPage("dashboard");}} style={{cursor:"pointer"}}><div style={{display:"flex",alignItems:"center",gap:8}}><img src={dark?"/logo-symbol-white.png":"/logo-symbol-navy.png"} alt="" style={{width:38,height:38,display:"block",flexShrink:0}}/><div style={{fontFamily:"'Quantico',sans-serif",fontWeight:700,height:38,display:"flex",flexDirection:"column",justifyContent:"space-between",color:dark?"#fff":"#1e3a5f",whiteSpace:"nowrap"}}><div style={{fontSize:20,lineHeight:1}}>SPRINGBLINDS</div><div style={{fontSize:15,lineHeight:1,letterSpacing:0.5}}>TEAM</div></div></div></div>
      <div className="sbn">
        <div className="nss">{lang==="kr"?"메뉴":lang==="es"?"Menú":"Menu"}</div>
        {nav.map(n=>(
          <div key={n.id} className={`ni ${page===n.id?"active":""}`} onClick={()=>setPage(n.id)}>
            <span className="nic">{n.icon}</span>{n.label}
          </div>
        ))}
        <hr className="div" style={{margin:"12px 0"}}/>
        <div className="ni" style={{cursor:"default"}}><span className="nic"><Globe size={17}/></span><LangSwitch lang={lang} setLang={setLang}/></div>
        <div className="ni" onClick={()=>setDark(!dark)}>
          <span className="nic">{dark?<Sun size={17}/>:<Moon size={17}/>}</span>{dark?t.lightBtn:t.darkBtn}
        </div>
        <div className="ni" onClick={onLogout}><span className="nic"><LogOut size={17}/></span>{t.nav.logout}</div>
        <div style={{padding:"21px 12px 6px",marginTop:12,borderTop:"1px solid var(--bd)"}}><AppFooter color="var(--tx2)" size={9} align="left"/><div style={{fontSize:10,color:"var(--tx3)",opacity:.6,marginTop:4}}>build {BUILD_ID}</div></div>
      </div>
    </div>
  );
}

const BUILD_ID=(((typeof document!=="undefined"?[...document.scripts].map(x=>x.src).find(u=>/main\.[a-z0-9]+\.js/.test(u)):"")||"").match(/main\.([a-z0-9]+)\.js/)||[])[1]||"dev";

function AppFooter({color,size=11,align="center",pad="0"}){
  return(
    <div style={{fontSize:size,lineHeight:1.7,color,textAlign:align,padding:pad,opacity:.9,whiteSpace:"nowrap"}}>
      <div>SPRINGBLINDS TEAM · Since Feb. 2026</div>
      <div>© 2026 Ultimate Products Global Inc.</div>
      <div>All rights reserved. · Built by AK</div>
    </div>
  );
}

function LangSwitch({lang,setLang}){
  const opts=[["kr","한국어"],["en","EN"],["es","ES"]];
  return (
    <div style={{display:"inline-flex",alignItems:"center",flexShrink:0,lineHeight:1}}>
      {opts.map(([k,l],i)=>(
        <button key={k} type="button" onClick={e=>{e.stopPropagation();setLang(k);}}
          style={{padding:i===0?"2px 9px 2px 0":"2px 9px",fontSize:13,fontWeight:lang===k?800:500,border:"none",
            borderLeft:i===0?"none":"1px solid var(--bd,#cbd5e1)",background:"transparent",cursor:"pointer",fontFamily:"inherit",
            color:lang===k?"var(--ac,#2563eb)":"var(--tx3,#64748b)"}}>{l}</button>
      ))}
    </div>
  );
}
